"use client";

import { useState } from "react";
import { X } from "lucide-react";

interface EmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string, marketingConsent: boolean) => void;
  isSending?: boolean;
}

export function EmailModal({
  isOpen,
  onClose,
  onSubmit,
  isSending = false,
}: EmailModalProps) {
  const [emailInput, setEmailInput] = useState("");
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const validateEmail = (email: string) => {
    return email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  };

  const handleSubmit = () => {
    // Validate email
    if (!emailInput.trim()) {
      setError("Please enter your email address");
      return;
    }

    if (!validateEmail(emailInput)) {
      setError("Please enter a valid email address");
      return;
    }

    // Submit email. Marketing opt-in has been removed, so consent is always false.
    onSubmit(emailInput.trim(), false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  // Close the modal on Escape (unless a send is in flight)
  const handleDialogKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" && !isSending) {
      e.stopPropagation();
      handleClose();
    }
  };

  const handleClose = () => {
    if (!isSending) {
      setEmailInput("");
      setError("");
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onKeyDown={handleDialogKeyDown}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-modal-title"
        className="relative w-full max-w-md bg-white dark:bg-plantista-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-plantista-700"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-plantista-700">
          <h2
            id="email-modal-title"
            className="text-xl font-bold text-plantista-950 dark:text-white font-display"
          >
            Send Design to Email
          </h2>
          {!isSending && (
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:text-plantista-400 dark:hover:text-plantista-200 rounded-lg hover:bg-gray-100 dark:hover:bg-plantista-800 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-sm text-gray-600 dark:text-plantista-300 mb-4">
            Enter your email to receive your personalized garden design. We'll
            send you all seasonal visualizations, plant recommendations, and
            shopping information after your design is generated.
          </p>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 dark:text-plantista-200 mb-2"
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  setError("");
                }}
                onKeyPress={handleKeyPress}
                disabled={isSending}
                placeholder="your@email.com"
                className="w-full px-4 py-3 border border-gray-300 dark:border-plantista-600 rounded-lg focus:ring-2 focus:ring-plantista-500 focus:border-transparent bg-white dark:bg-plantista-800 text-plantista-950 dark:text-white placeholder-gray-400 dark:placeholder-plantista-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                autoFocus
              />
              {error && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-plantista-700">
          <button
            onClick={handleClose}
            disabled={isSending}
            className="px-4 py-2 text-gray-700 dark:text-plantista-300 hover:bg-gray-100 dark:hover:bg-plantista-800 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSending}
            className="px-8 py-3 text-plantista-950 font-bold rounded-lg transition-all bg-plantista-400 dark:bg-plantista-400 hover:bg-plantista-500 dark:hover:bg-plantista-300 shadow-[4px_4px_0px_0px] shadow-plantista-950 dark:shadow-plantista-50 border-2 border-plantista-950 active:translate-y-[2px] active:translate-x-[2px] active:shadow-[2px_2px_0px_0px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSending ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Sending...
              </>
            ) : (
              "Continue to Generate"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
